import type {
  HTMLInputTypeAttribute,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "./icon";

export interface TextInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  icon?: IconName;
  disabled?: boolean;
  /** Lower/upper bounds, forwarded to the native input (e.g. `min`/`max` for date or number). */
  min?: string | number;
  max?: string | number;
  /** Step granularity, forwarded to the native input (e.g. for number/date). */
  step?: string | number;
  /** Extra class applied to the input wrapper (e.g. `flex:1` for a composer). */
  className?: string;
  /** Extra class applied to the native input itself (e.g. `mono` for tabular figures). */
  inputClassName?: string;
  /** Forwarded to the native input. */
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  /** Forwarded to the native input. */
  autoFocus?: boolean;
  /** Forwarded to the native input. */
  autoComplete?: string;
  /** Forwarded to the native input. */
  id?: string;
  /** Forwarded to the native input. */
  name?: string;
  /** Forwarded to the native input. */
  inputMode?: "text" | "none" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = "text",
  icon,
  disabled,
  min,
  max,
  step,
  className,
  inputClassName,
  onKeyDown,
  autoFocus,
  autoComplete,
  id,
  name,
  inputMode,
}: TextInputProps) {
  return (
    <div className={cn("input-wrap", icon && "has-icon", className)}>
      {icon && <Icon name={icon} size={16} className="input-icon" />}
      <input
        className={cn("input", inputClassName)}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        id={id}
        name={name}
        inputMode={inputMode}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  );
}
